import React, { useEffect, useState } from "react";
import "./MetroMissionControl.css";

const API = "/api/metro";

/* =========================================================
   SMALL COMPONENTS
   ========================================================= */

function MetricRow({ label, value, status }) {
    return (
        <div className="metric-row">
            <span>{label}</span>

            <strong className={status || ""}>
                {value}
            </strong>
        </div>
    );
}


function StatusDot({ status }) {
    return (
        <span className={`status-dot ${status || ""}`}>
            ●
        </span>
    );
}


function PanelTitle({ title }) {
    return (
        <div className="panel-title">
            <span />
            <h2>{title}</h2>
        </div>
    );
}


function StatusBadge({ status }) {
    return (
        <span
            className={`status-badge ${String(
                status || ""
            ).toLowerCase()}`}
        >
            ● {status}
        </span>
    );
}


function RiskBar({ label, value }) {
    let level = "low";

    if (value >= 80) {
        level = "high";
    } else if (value >= 60) {
        level = "medium";
    }

    return (
        <div className="risk-bar-container">

            <div className="risk-label">
                <span>{label}</span>

                <strong>
                    {value}%
                </strong>
            </div>

            <div className="risk-bar">
                <div
                    className={level}
                    style={{
                        width: `${value}%`
                    }}
                />
            </div>

        </div>
    );
}


/* =========================================================
   MAIN COMPONENT
   ========================================================= */

export default function MetroMissionControl() {

    /* =====================================================
       SYSTEM
       ===================================================== */

    const [system, setSystem] = useState({
        status: "OPERATIONAL",
        line: "RED LINE",
        trains: 18,
        stations: 32,
        sensors: 184,
        drones: 4
    });


    /* =====================================================
       GROUND
       ===================================================== */

    const [ground, setGround] = useState({
        displacement: 18.4,
        velocity: 2.1,
        tilt: 0.003,
        settlement: 7.2,
        status: "WARNING"
    });


    /* =====================================================
       SEISMIC
       ===================================================== */

    const [seismic, setSeismic] = useState({
        activity: "ELEVATED",
        acceleration: 0.08,
        velocity: 12.4,
        magnitude: 3.2,
        distance: 48,
        earlyWarning: "STANDBY"
    });


    /* =====================================================
       TRACKS
       ===================================================== */

    const [tracks, setTracks] = useState([
        {
            id: "TRACK-01",
            name: "Track 01",
            status: "NORMAL",
            displacement: 2.1,
            vibration: "NORMAL"
        },
        {
            id: "TRACK-02",
            name: "Track 02",
            status: "NORMAL",
            displacement: 1.4,
            vibration: "NORMAL"
        },
        {
            id: "TRACK-03",
            name: "Track 03",
            status: "WARNING",
            displacement: 18.4,
            vibration: "ELEVATED"
        },
        {
            id: "TRACK-04",
            name: "Track 04",
            status: "NORMAL",
            displacement: 2.4,
            vibration: "NORMAL"
        }
    ]);


    /* =====================================================
       STRUCTURES
       ===================================================== */

    const [structures, setStructures] = useState([
        {
            id: "BRIDGE-01",
            name: "Bridge 01",
            type: "Bridge",
            status: "NORMAL",
            vibration: 0.12
        },
        {
            id: "BRIDGE-02",
            name: "Bridge 02",
            type: "Bridge",
            status: "WARNING",
            vibration: 0.72
        },
        {
            id: "TUNNEL-01",
            name: "Tunnel 01",
            type: "Tunnel",
            status: "NORMAL",
            vibration: 0.08
        },
        {
            id: "STATION-14",
            name: "Station 14",
            type: "Station",
            status: "NORMAL",
            vibration: 0.04
        }
    ]);


    /* =====================================================
       DRONES
       ===================================================== */

    const [drones, setDrones] = useState([
        {
            id: "DRONE-01",
            status: "AIRBORNE",
            mission: "Track inspection",
            battery: 73,
            altitude: 47.2
        },
        {
            id: "DRONE-02",
            status: "CHARGING",
            mission: "Standby",
            battery: 42,
            altitude: 0
        },
        {
            id: "DRONE-03",
            status: "AIRBORNE",
            mission: "Bridge inspection",
            battery: 81,
            altitude: 52.8
        },
        {
            id: "DRONE-04",
            status: "OFFLINE",
            mission: "None",
            battery: 0,
            altitude: 0
        }
    ]);


    /* =====================================================
       RISK
       ===================================================== */

    const [risk, setRisk] = useState({
        ground: 84,
        track: 67,
        bridge: 91,
        seismic: 61,
        weather: 33,
        overall: "HIGH",
        confidence: 87
    });


    /* =====================================================
       ALERTS
       ===================================================== */

    const [alerts, setAlerts] = useState([
        {
            id: 1,
            severity: "HIGH",
            asset: "TRACK-014",
            message: "Ground displacement detected",
            time: "14:31:51"
        },
        {
            id: 2,
            severity: "WARNING",
            asset: "BRIDGE-02",
            message: "Abnormal vibration detected",
            time: "14:31:34"
        },
        {
            id: 3,
            severity: "WARNING",
            asset: "SEISMIC-04",
            message: "Elevated seismic activity",
            time: "14:31:22"
        }
    ]);


    /* =====================================================
       LOGS
       ===================================================== */

    const [logs, setLogs] = useState([
        {
            time: "14:31:51",
            event: "sensor.track.014.anomaly_detected",
            message: "Ground displacement detected"
        },
        {
            time: "14:31:54",
            event: "drone.03.inspection.started",
            message: "Bridge inspection started"
        },
        {
            time: "14:32:01",
            event: "seismic.activity.elevated",
            message: "Seismic activity elevated"
        },
        {
            time: "14:32:08",
            event: "infrastructure.risk.updated",
            message: "Infrastructure risk updated"
        }
    ]);


    /* =====================================================
       SELECTED ASSET
       ===================================================== */

    const [selectedAsset, setSelectedAsset] =
        useState(null);


    /* =========================================================
       INITIAL DASHBOARD DATA
       ========================================================= */

    useEffect(() => {

        let mounted = true;

        fetch(`${API}/dashboard`)
            .then(response => {

                if (!response.ok) {
                    throw new Error(
                        `HTTP ${response.status}`
                    );
                }

                return response.json();
            })
            .then(data => {

                if (!mounted) {
                    return;
                }

                if (data.system) {
                    setSystem(data.system);
                }

                if (data.ground) {
                    setGround(data.ground);
                }

                if (data.seismic) {
                    setSeismic(data.seismic);
                }

                if (data.tracks) {
                    setTracks(data.tracks);
                }

                if (data.structures) {
                    setStructures(data.structures);
                }

                if (data.drones) {
                    setDrones(data.drones);
                }

                if (data.risk) {
                    setRisk(data.risk);
                }

                if (data.alerts) {
                    setAlerts(data.alerts);
                }

                if (data.logs) {
                    setLogs(data.logs);
                }
            })
            .catch(error => {

                console.error(
                    "Dashboard error:",
                    error
                );
            });

        return () => {
            mounted = false;
        };

    }, []);


    /* =========================================================
       SOCKET.IO
       ========================================================= */

    useEffect(() => {

        const socket = io(
            window.location.origin,
            {
                transports: ["polling"]
            }
        );


        socket.on("connect", () => {

            addLog(
                "system.websocket.connected",
                "Mission Control connected"
            );

        });


        socket.on(
            "metro.telemetry",
            data => {

                if (data.system) {
                    setSystem(data.system);
                }

                if (data.ground) {
                    setGround(data.ground);
                }

                if (data.seismic) {
                    setSeismic(data.seismic);
                }

                if (data.risk) {
                    setRisk(data.risk);
                }

                if (data.tracks) {
                    setTracks(data.tracks);
                }

                if (data.structures) {
                    setStructures(data.structures);
                }

                if (data.drones) {
                    setDrones(data.drones);
                }

            }
        );


        socket.on(
            "metro.alert",
            alert => {

                setAlerts(previous => [
                    alert,
                    ...previous
                ].slice(0, 20));


                addLog(
                    "infrastructure.alert.created",
                    alert.message
                );

            }
        );


        socket.on("disconnect", () => {

            addLog(
                "system.websocket.disconnected",
                "Mission Control disconnected"
            );

        });


        return () => {

            socket.disconnect();

        };

    }, []);


    /* =========================================================
       LOGGING
       ========================================================= */

    function addLog(event, message) {

        const now =
            new Date().toLocaleTimeString(
                [],
                {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit"
                }
            );


        setLogs(previous => [

            {
                time: now,
                event,
                message
            },

            ...previous

        ].slice(0, 100));
    }


    /* =========================================================
       DRONE COMMAND
       ========================================================= */

    async function dispatchDrone(
        droneId,
        assetId
    ) {

        try {

            const response = await fetch(
                `${API}/drones/${droneId}/mission`,
                {
                    method: "POST",

                    headers: {
                        "Content-Type":
                            "application/json"
                    },

                    body: JSON.stringify({

                        asset_id:
                            assetId,

                        mission_type:
                            "infrastructure_inspection"

                    })
                }
            );


            if (!response.ok) {

                throw new Error(
                    `HTTP ${response.status}`
                );

            }


            const data =
                await response.json();


            if (data.success) {

                addLog(
                    "drone.mission.started",
                    `${droneId} dispatched to ${assetId}`
                );

            }

        }
        catch (error) {

            console.error(
                "Drone dispatch error:",
                error
            );

            addLog(
                "drone.mission.error",
                `${droneId} dispatch failed`
            );

        }
    }


    /* =========================================================
       RENDER
       ========================================================= */

    return (

        <div className="metro-control">


            {/* =================================================
                HEADER
            ================================================= */}

            <header className="metro-header">

                <div className="header-title">

                    <div className="system-name">
                        STEMX365
                    </div>

                    <div>

                        <h1>
                            METRO INFRASTRUCTURE
                            MISSION CONTROL
                        </h1>

                        <p>
                            Intelligent Rail &
                            Infrastructure Monitoring
                        </p>

                    </div>

                </div>


                <div className="header-status">

                    <div>

                        <StatusDot status="online" />

                        {system.status}

                    </div>


                    <div>
                        {system.line}
                    </div>


                    <div>
                        {system.stations}
                        {" "}STATIONS
                    </div>


                    <div>
                        {system.sensors}
                        {" "}SENSORS
                    </div>


                    <div className="clock">

                        {new Date().toLocaleTimeString()}

                    </div>

                </div>

            </header>


            {/* =================================================
                ALERT BAR
            ================================================= */}

            <div className="alert-bar">

                <div className="alert-bar-title">
                    ACTIVE ALERTS
                </div>


                <div className="alert-summary">

                    <span className="critical-count">

                        {
                            alerts.filter(
                                a =>
                                    a.severity ===
                                    "CRITICAL"
                            ).length
                        }

                        {" "}CRITICAL

                    </span>


                    <span className="high-count">

                        {
                            alerts.filter(
                                a =>
                                    a.severity ===
                                    "HIGH"
                            ).length
                        }

                        {" "}HIGH

                    </span>


                    <span className="warning-count">

                        {
                            alerts.filter(
                                a =>
                                    a.severity ===
                                    "WARNING"
                            ).length
                        }

                        {" "}WARNING

                    </span>

                </div>

            </div>


            {/* =================================================
                MAIN GRID
            ================================================= */}

            <main className="metro-grid">


                {/* =================================================
                    NETWORK STATUS
                ================================================= */}

                <section className="metro-panel network-panel">

                    <PanelTitle
                        title="NETWORK STATUS"
                    />


                    <MetricRow
                        label="Stations"
                        value={`${system.stations} / ${system.stations}`}
                        status="good"
                    />


                    <MetricRow
                        label="Track Sensors"
                        value={`${system.sensors} / 190`}
                        status="good"
                    />


                    <MetricRow
                        label="Seismic Sensors"
                        value="12 / 12"
                        status="good"
                    />


                    <MetricRow
                        label="Drones"
                        value={`${system.drones} / 5`}
                        status="warning-text"
                    />


                    <MetricRow
                        label="Weather Stations"
                        value="8 / 8"
                        status="good"
                    />


                    <MetricRow
                        label="Train Fleet"
                        value={`${system.trains} ACTIVE`}
                        status="good"
                    />

                </section>


                {/* =================================================
                    DIGITAL MAP
                ================================================= */}

                <section className="metro-panel map-panel">

                    <PanelTitle
                        title="METRO DIGITAL TWIN"
                    />


                    <div className="metro-map">

                        <div className="map-grid" />


                        <div className="map-line red-line">

                            <div
                                className="station"
                                onClick={() =>
                                    setSelectedAsset(
                                        "STATION-01"
                                    )
                                }
                            >
                                01
                            </div>


                            <div
                                className="station"
                                onClick={() =>
                                    setSelectedAsset(
                                        "STATION-02"
                                    )
                                }
                            >
                                02
                            </div>


                            <div
                                className="station warning-station"
                                onClick={() =>
                                    setSelectedAsset(
                                        "TRACK-014"
                                    )
                                }
                            >
                                03
                            </div>


                            <div
                                className="station"
                                onClick={() =>
                                    setSelectedAsset(
                                        "STATION-04"
                                    )
                                }
                            >
                                04
                            </div>


                            <div
                                className="station"
                                onClick={() =>
                                    setSelectedAsset(
                                        "STATION-05"
                                    )
                                }
                            >
                                05
                            </div>

                        </div>


                        <div className="bridge-marker">
                            B2
                        </div>


                        <div className="tunnel-marker">
                            T1
                        </div>


                        <div className="drone-marker drone-one">
                            D1
                        </div>


                        <div className="drone-marker drone-three">
                            D3
                        </div>


                        <div className="map-label map-label-a">
                            NORTH
                        </div>


                        <div className="map-label map-label-b">
                            BRIDGE 02
                        </div>


                        <div className="map-label map-label-c">
                            TUNNEL 01
                        </div>

                    </div>


                    {selectedAsset && (

                        <div className="selected-asset">

                            SELECTED:

                            <strong>
                                {selectedAsset}
                            </strong>


                            <button
                                type="button"
                                onClick={() =>
                                    setSelectedAsset(null)
                                }
                            >
                                ×
                            </button>

                        </div>

                    )}

                </section>


                {/* =================================================
                    ALERT CENTER
                ================================================= */}

                <section className="metro-panel alert-panel">

                    <PanelTitle
                        title="ALERT CENTER"
                    />


                    <div className="alert-list">

                        {alerts.map(alert => (

                            <div
                                className={`alert-item ${String(
                                    alert.severity || ""
                                ).toLowerCase()}`}
                                key={alert.id}
                            >

                                <div className="alert-icon">
                                    !
                                </div>


                                <div className="alert-content">

                                    <div className="alert-top">

                                        <strong>
                                            {alert.asset}
                                        </strong>


                                        <span>
                                            {alert.time}
                                        </span>

                                    </div>


                                    <div>
                                        {alert.message}
                                    </div>

                                </div>

                            </div>

                        ))}

                    </div>

                </section>


                {/* =================================================
                    GROUND MOVEMENT
                ================================================= */}

                <section className="metro-panel">

                    <PanelTitle
                        title="GROUND MOVEMENT"
                    />


                    <MetricRow
                        label="Displacement"
                        value={`${Number(
                            ground.displacement
                        ).toFixed(1)} mm`}
                        status="warning-text"
                    />


                    <MetricRow
                        label="Velocity"
                        value={`${Number(
                            ground.velocity
                        ).toFixed(1)} mm/day`}
                    />


                    <MetricRow
                        label="Tilt"
                        value={`${Number(
                            ground.tilt
                        ).toFixed(3)}°`}
                    />


                    <MetricRow
                        label="Settlement"
                        value={`${Number(
                            ground.settlement
                        ).toFixed(1)} mm`}
                    />


                    <div className="risk-status warning">

                        {ground.status}

                    </div>

                </section>


                {/* =================================================
                    SEISMIC
                ================================================= */}

                <section className="metro-panel">

                    <PanelTitle
                        title="SEISMIC MONITORING"
                    />


                    <div className="seismic-state">

                        <span>
                            ACTIVITY
                        </span>


                        <strong>
                            {seismic.activity}
                        </strong>

                    </div>


                    <MetricRow
                        label="Acceleration"
                        value={`${Number(
                            seismic.acceleration
                        ).toFixed(2)} g`}
                    />


                    <MetricRow
                        label="Ground Velocity"
                        value={`${Number(
                            seismic.velocity
                        ).toFixed(1)} mm/s`}
                    />


                    <MetricRow
                        label="Magnitude Estimate"
                        value={`M ${Number(
                            seismic.magnitude
                        ).toFixed(1)}`}
                    />


                    <MetricRow
                        label="Distance"
                        value={`${seismic.distance} km`}
                    />


                    <MetricRow
                        label="Early Warning"
                        value={seismic.earlyWarning}
                    />

                </section>


                {/* =================================================
                    TRACK HEALTH
                ================================================= */}

                <section className="metro-panel wide-panel">

                    <PanelTitle
                        title="TRACK HEALTH"
                    />


                    <div className="asset-table">

                        <div className="table-header">

                            <span>TRACK</span>

                            <span>STATUS</span>

                            <span>
                                DISPLACEMENT
                            </span>

                            <span>
                                VIBRATION
                            </span>

                            <span>ACTION</span>

                        </div>


                        {tracks.map(track => (

                            <div
                                className="table-row"
                                key={track.id}
                            >

                                <span>
                                    {track.name}
                                </span>


                                <span>

                                    <StatusBadge
                                        status={
                                            track.status
                                        }
                                    />

                                </span>


                                <span>
                                    {track.displacement}
                                    {" "}mm
                                </span>


                                <span>
                                    {track.vibration}
                                </span>


                                <span>

                                    {track.status !==
                                        "NORMAL" && (

                                        <button
                                            type="button"
                                            className="small-button"
                                            onClick={() =>
                                                dispatchDrone(
                                                    "DRONE-01",
                                                    track.id
                                                )
                                            }
                                        >
                                            INSPECT
                                        </button>

                                    )}

                                </span>

                            </div>

                        ))}

                    </div>

                </section>


                {/* =================================================
                    STRUCTURAL HEALTH
                ================================================= */}

                <section className="metro-panel wide-panel">

                    <PanelTitle
                        title="STRUCTURAL HEALTH"
                    />


                    <div className="asset-table">

                        <div className="table-header">

                            <span>ASSET</span>

                            <span>TYPE</span>

                            <span>STATUS</span>

                            <span>VIBRATION</span>

                            <span>ACTION</span>

                        </div>


                        {structures.map(
                            structure => (

                                <div
                                    className="table-row"
                                    key={structure.id}
                                >

                                    <span>
                                        {structure.name}
                                    </span>


                                    <span>
                                        {structure.type}
                                    </span>


                                    <span>

                                        <StatusBadge
                                            status={
                                                structure.status
                                            }
                                        />

                                    </span>


                                    <span>
                                        {
                                            structure.vibration
                                        }
                                        {" "}g
                                    </span>


                                    <span>

                                        {
                                            structure.status !==
                                                "NORMAL" && (
                                                <button
                                                    type="button"
                                                    className="small-button"
                                                    onClick={() =>
                                                        dispatchDrone(
                                                            "DRONE-03",
                                                            structure.id
                                                        )
                                                    }
                                                >
                                                    DRONE
                                                </button>
                                            )
                                        }

                                    </span>

                                </div>

                            )
                        )}

                    </div>

                </section>


                {/* =================================================
                    DRONE FLEET
                ================================================= */}

                <section className="metro-panel wide-panel">

                    <PanelTitle
                        title="DRONE FLEET"
                    />


                    <div className="drone-grid">

                        {drones.map(drone => (

                            <div
                                className="drone-card"
                                key={drone.id}
                            >

                                <div className="drone-header">

                                    <strong>
                                        {drone.id}
                                    </strong>


                                    <StatusBadge
                                        status={
                                            drone.status
                                        }
                                    />

                                </div>


                                <div className="drone-icon">
                                    ◈
                                </div>


                                <MetricRow
                                    label="Mission"
                                    value={
                                        drone.mission
                                    }
                                />


                                <MetricRow
                                    label="Battery"
                                    value={`${drone.battery}%`}
                                />


                                <MetricRow
                                    label="Altitude"
                                    value={`${drone.altitude} m`}
                                />

                            </div>

                        ))}

                    </div>

                </section>


                {/* =================================================
                    RISK ENGINE
                ================================================= */}

                <section className="metro-panel risk-panel">

                    <PanelTitle
                        title="INFRASTRUCTURE RISK ENGINE"
                    />


                    <RiskBar
                        label="Ground Movement"
                        value={risk.ground}
                    />


                    <RiskBar
                        label="Track Deformation"
                        value={risk.track}
                    />


                    <RiskBar
                        label="Bridge Vibration"
                        value={risk.bridge}
                    />


                    <RiskBar
                        label="Seismic Activity"
                        value={risk.seismic}
                    />


                    <RiskBar
                        label="Weather / Flood"
                        value={risk.weather}
                    />


                    <div className="overall-risk">

                        <span>
                            OVERALL RISK
                        </span>


                        <strong>
                            {risk.overall}
                        </strong>

                    </div>


                    <div className="confidence">

                        <span>
                            AI CONFIDENCE
                        </span>


                        <strong>
                            {risk.confidence}%
                        </strong>

                    </div>

                </section>


                {/* =================================================
                    MISSION LOG
                ================================================= */}

                <section className="metro-panel log-panel">

                    <PanelTitle
                        title="MISSION / EVENT LOG"
                    />


                    <div className="mission-log">

                        {logs.map(
                            (log, index) => (

                                <div
                                    className="log-entry"
                                    key={`${log.time}-${index}`}
                                >

                                    <span>
                                        {log.time}
                                    </span>


                                    <strong>
                                        {log.event}
                                    </strong>


                                    <label>
                                        {log.message}
                                    </label>

                                </div>

                            )
                        )}

                    </div>

                </section>

            </main>

        </div>
    );
}